/**
 * Integration definitions & types — shared across Settings, DiscoveryPanel,
 * OnboardingWizard, and any future integration-related UI.
 *
 * Previously inlined in Settings.tsx. Extracted to enable reuse without
 * importing the entire Settings component (clean architecture refactor).
 *
 * Also exports API wrapper functions for Gmail / signals so consuming
 * components can import everything integration-related from a single module.
 */

import { api } from './api';
import type { SourceCoverage } from '@waypoint/shared';

export type ConnectFlow = 'oauth' | 'api_key' | 'webhook_url' | 'external' | 'coming_soon' | 'universal';

export interface IntegrationDef {
  id: string;
  provider: string;
  name: string;
  icon: string;
  description: string;
  flow: ConnectFlow;
  keyPlaceholder?: string;
  keyHint?: string;
  /** Optional second input on the api_key modal (e.g. GHL Location ID). */
  secondaryField?: { bodyKey: string; placeholder: string; hint: string; optional?: boolean };
  dependsOn?: string;
  /** Google OAuth scope bundle this tile requests (`SCOPE_SETS` key in
   *  gmail-oauth.ts). Omitted → the API's own default (`core`). */
  scopes?: string;
  /** In-app screen showing this source's data; a connected tile links to it. */
  hubPath?: string;
  /** Which entry in the API's GOOGLE_CONNECTORS registry this tile starts.
   *  The scope list lives on the server; the client only names the connector,
   *  so a tile cannot request anything wider than that connector allows. */
  googleConnector?: string;
  /** Not shown in the Settings catalogue unless already connected. The
   *  connector still exists server-side. */
  hidden?: boolean;
}

export interface ApiIntegration {
  provider: string;
  status: string;
  external_account_id: string | null;
  external_account_label: string | null;
  scope: string | null;
  connected_at: string | null;
  last_synced_at: string | null;
  last_sync_status: string | null;
}

/** Where to verify connected account on the provider's console. */
export const PROVIDER_VERIFY_URL: Record<string, string> = {
  gmail:        'https://mail.google.com',
  calendar:     'https://calendar.google.com',
  quickbooks:   'https://app.qbo.intuit.com',
  stripe:       'https://dashboard.stripe.com',
  hubspot:      'https://app.hubspot.com',
  close:        'https://app.close.com',
  slack:        'https://app.slack.com',
  teams:        'https://teams.microsoft.com',
  zoom:         'https://zoom.us/profile',
  fireflies:    'https://app.fireflies.ai',
  linear:       'https://linear.app',
  notion:       'https://www.notion.so',
  mailchimp:    'https://admin.mailchimp.com',
  apollo:       'https://app.apollo.io',
  zapier:       'https://zapier.com/app/dashboard',
  gohighlevel:  'https://app.gohighlevel.com',
  monday:       'https://monday.com',
  salesforce:   'https://login.salesforce.com',
  xero:         'https://go.xero.com',
  pipedrive:    'https://app.pipedrive.com',
  calendly:     'https://calendly.com/app',
  shopify:      'https://admin.shopify.com',
  square:       'https://squareup.com/dashboard',
  woocommerce:  'https://wordpress.com/home',
  paypal:       'https://www.paypal.com/businessmanage',
  chargebee:    'https://app.chargebee.com',
  recurly:      'https://app.recurly.com',
  sap:          'https://help.sap.com/docs/SAP_BUSINESS_ONE',
  zendesk:      'https://www.zendesk.com/login',
  intercom:     'https://app.intercom.com',
  freshdesk:    'https://freshdesk.com/login',
  klaviyo:      'https://www.klaviyo.com/dashboard',
  calcom:       'https://app.cal.com',
  acuity:       'https://secure.acuityscheduling.com',
  pandadoc:     'https://app.pandadoc.com',
  zoho:         'https://crm.zoho.com',
  asana:        'https://app.asana.com',
  clickup:      'https://app.clickup.com',
  activecampaign: 'https://www.activecampaign.com/login',
  instantly:    'https://app.instantly.ai',
  gong:         'https://app.gong.io',
  agiloft:      'https://help.agiloft.com/',
  webex:        'https://developer.webex.com/my-apps',
  adobesign:    'https://acrobat.adobe.com/',
  clearbit:     'https://dashboard.clearbit.com',
  clay:         'https://app.clay.com',
  n8n:          'https://app.n8n.cloud',
  constantcontact: 'https://app.constantcontact.com',
  freshbooks:   'https://my.freshbooks.com',
  double:       'https://app.doublehq.com',
};

// hidden until a unified invoices table exists; these write into stripe_invoices
const HIDDEN = { hidden: true } as const;

/**
 * Connectors that work but have not been exercised against a live tenant.
 * The tile shows "Beta — request access" instead of Connect; a connection
 * that already exists keeps its normal Sync/Disconnect controls.
 */
export const BETA_REQUEST_ACCESS = new Set<string>([
  'mailchimp', 'klaviyo', 'activecampaign', 'instantly', 'clearbit', 'clay',
  'constantcontact', 'apollo', 'zendesk', 'intercom', 'freshdesk', 'linear',
  'notion', 'asana', 'clickup', 'pandadoc', 'agiloft', 'calendly', 'calcom',
  'acuity', 'gong', 'double', 'sap',
]);
export const BETA_REQUEST_MAILTO = (name: string): string =>
  `mailto:support@waypoint-intel.com?subject=${encodeURIComponent(`Beta access: ${name}`)}`;

/** All supported native integrations. Order determines display order. */
export const INTEGRATION_DEFS: IntegrationDef[] = [
  { id: 'hubspot', provider: 'hubspot', name: 'HubSpot', icon: '🟠', description: 'CRM data, deals, and contacts', flow: 'oauth' },
  { id: 'quickbooks', provider: 'quickbooks', name: 'QuickBooks', icon: '🟢', description: 'Revenue, invoices, and AR aging', flow: 'oauth' },
  { id: 'gmail', provider: 'gmail', name: 'Gmail', icon: '📧', description: 'Email signals and send-on-approve drafts', flow: 'oauth', googleConnector: 'gmail' },
  // Google marketing sources. Each connects on its own and requests only its
  // own permission (see GOOGLE_CONNECTORS in apps/api) — connecting YouTube
  // cannot grant Search Console. Listed here as well as in the Marketing hub
  // because Settings -> Integrations is where people look for a connector.
  { id: 'gsc', provider: 'gsc', name: 'Google Search Console', icon: '🔍', description: 'Search Analytics — cannibalization, keyword opportunities, content decay', flow: 'oauth', googleConnector: 'gsc', hubPath: '/dashboard/marketing?source=gsc' },
  { id: 'ga4', provider: 'ga4', name: 'Google Analytics 4', icon: '📈', description: 'Sessions and conversions by channel, attributed to closed deals', flow: 'oauth', googleConnector: 'ga4', hubPath: '/dashboard/marketing?source=ga4' },
  { id: 'youtube', provider: 'youtube', name: 'YouTube', icon: '▶️', description: 'Channel and recent upload performance in the Social Media card', flow: 'oauth', googleConnector: 'youtube', hubPath: '/dashboard/marketing?source=youtube' },
  { id: 'gbp', provider: 'gbp', name: 'Google Business Profile', icon: '🏪', description: 'Star ratings and recent reviews across your locations', flow: 'oauth', googleConnector: 'gbp', hubPath: '/dashboard/marketing?source=gbp' },
  { id: 'google_cal', provider: 'calendar', name: 'Google Calendar', icon: '📅', description: 'Meeting prep from your calendar', flow: 'oauth', googleConnector: 'calendar' },
  { id: 'outlook', provider: 'outlook', name: 'Outlook / Microsoft 365', icon: '📨', description: 'Email + calendar intelligence, follow-up detection, send-on-approve', flow: 'oauth' },
  { id: 'outlook_cal', provider: 'outlook_calendar', name: 'Outlook Calendar', icon: '📆', description: 'Meeting sync + prep signals via Microsoft Graph', flow: 'oauth', dependsOn: 'outlook' },
  { id: 'teams', provider: 'teams', name: 'Microsoft Teams', icon: '🟣', description: 'Commitments made in 1:1 and group chats tracked until kept — opt-in, adds Chat.Read to your Microsoft consent', flow: 'oauth', dependsOn: 'outlook' },
  { id: 'stripe', provider: 'stripe', name: 'Stripe', icon: '💳', description: 'Payment tracking and billing', flow: 'api_key', keyPlaceholder: 'rk_live_... or sk_live_...', keyHint: 'Paste a restricted read-only API key from Stripe Dashboard → Developers → API keys' },
  { id: 'slack', provider: 'slack', name: 'Slack', icon: '💬', description: 'Slack notifications, commands, and conversation sync', flow: 'oauth' },
  { id: 'linear', provider: 'linear', name: 'Linear', icon: '🔷', description: 'Task and project tracking', flow: 'oauth' },
  { id: 'notion', provider: 'notion', name: 'Notion', icon: '📝', description: 'Knowledge base and docs', flow: 'oauth' },
  { id: 'fireflies', provider: 'fireflies', name: 'Fireflies.ai', icon: '🔥', description: 'Meeting transcripts + AI analysis (captures Zoom, Meet, Teams)', flow: 'api_key', keyPlaceholder: 'Your Fireflies API key...', keyHint: 'Get your API key from Fireflies → Settings → Developer API → Copy API Key' },
  { id: 'zoom', provider: 'zoom', name: 'Zoom', icon: '📹', description: 'Captured via Fireflies — connect Fireflies.ai and Zoom meetings arrive automatically', flow: 'coming_soon' },
  { id: 'gohighlevel', provider: 'gohighlevel', name: 'GoHighLevel', icon: '🚀', description: 'CRM, pipeline, contacts, and marketing automation', flow: 'api_key', keyPlaceholder: 'pit-... private integration token', keyHint: 'GoHighLevel → Settings → Private Integrations → create a token with the View Contacts and View Opportunities scopes (legacy API keys also work)', secondaryField: { bodyKey: 'location_id', placeholder: 'Location ID (required for pit-... tokens)', hint: 'GoHighLevel → Settings → Business Profile → Location ID', optional: true } },
  { id: 'salesforce', provider: 'salesforce', name: 'Salesforce', icon: '☁️', description: 'Enterprise CRM — contacts, opportunities, and accounts', flow: 'oauth' },
  { id: 'xero', provider: 'xero', name: 'Xero', icon: '🔵', description: 'Accounting — invoices, payments, and contacts', flow: 'oauth', ...HIDDEN },
  { id: 'monday', provider: 'monday', name: 'Monday.com', icon: '📋', description: 'Project management, boards, and team workflows', flow: 'api_key', keyPlaceholder: 'Your Monday.com API token...', keyHint: 'monday.com → click your avatar → Developers → My access tokens → Copy (admin/member personal token)' },
  { id: 'apollo', provider: 'apollo', name: 'Apollo.io', icon: '🚀', description: 'Sales intelligence, contacts, and prospecting', flow: 'api_key', keyPlaceholder: 'Your Apollo API key...', keyHint: 'Get your API key from Apollo → Settings → Integrations → API' },
  { id: 'mailchimp', provider: 'mailchimp', name: 'Mailchimp', icon: '🐵', description: 'Email marketing campaigns and audience insights', flow: 'oauth' },
  { id: 'close', provider: 'close', name: 'Close CRM', icon: '📞', description: 'Sales CRM — leads, opportunities, and activities', flow: 'api_key', keyPlaceholder: 'api_...', keyHint: 'Get your API key from Close → Settings → Developer → API Keys' },
  { id: 'pipedrive', provider: 'pipedrive', name: 'Pipedrive', icon: '🟩', description: 'Sales CRM — deals, contacts, and pipelines', flow: 'api_key', keyPlaceholder: 'Your Pipedrive API token...', keyHint: 'Pipedrive → click your avatar → Personal preferences → API → copy your personal API token' },
  { id: 'calendly', provider: 'calendly', name: 'Calendly', icon: '🗓️', description: 'Scheduled meetings, cancellations, and booking signals', flow: 'api_key', keyPlaceholder: 'Your Calendly personal access token...', keyHint: 'Calendly → Integrations & apps → API & webhooks → Generate a personal access token' },
  { id: 'shopify', provider: 'shopify', name: 'Shopify', icon: '🛍️', description: 'Orders, customers, and store revenue', flow: 'api_key', keyPlaceholder: 'shpat_...', keyHint: 'Shopify admin → Settings → Apps and sales channels → Develop apps → create an app with read_orders + read_customers → install → copy the Admin API access token', secondaryField: { bodyKey: 'shop_domain', placeholder: 'your-store.myshopify.com', hint: 'Your .myshopify.com domain (Settings → Domains)' }, ...HIDDEN },
  { id: 'square', provider: 'square', name: 'Square', icon: '⬛', description: 'Payments, customers, and point-of-sale revenue', flow: 'api_key', keyPlaceholder: 'EAAA...', keyHint: 'developer.squareup.com → your application → Production → copy the access token', ...HIDDEN },
  { id: 'woocommerce', provider: 'woocommerce', name: 'WooCommerce', icon: '🛒', description: 'Store orders, customers, and revenue', flow: 'api_key', keyPlaceholder: 'ck_...:cs_...', keyHint: 'WooCommerce → Settings → Advanced → REST API → create a Read key, then paste "consumer key : consumer secret" joined by a colon', secondaryField: { bodyKey: 'site_url', placeholder: 'https://store.example.com', hint: 'Your store URL' }, ...HIDDEN },
  { id: 'paypal', provider: 'paypal', name: 'PayPal', icon: '💰', description: 'Transactions and payer revenue', flow: 'api_key', keyPlaceholder: 'client_id:secret', keyHint: 'developer.paypal.com → Apps & Credentials → your live REST app → paste "client ID : secret" joined by a colon', ...HIDDEN },
  { id: 'chargebee', provider: 'chargebee', name: 'Chargebee', icon: '🔁', description: 'Subscription invoices, customers, and AR aging', flow: 'api_key', keyPlaceholder: 'live_...', keyHint: 'Chargebee → Settings → Configure Chargebee → API keys → create a read-only key', secondaryField: { bodyKey: 'site', placeholder: 'your-site', hint: 'The {site} in {site}.chargebee.com' }, ...HIDDEN },
  { id: 'sap', provider: 'sap', name: 'SAP', icon: '🏭', description: 'ERP customers, receivables, and AR aging — Business One and S/4HANA', flow: 'api_key', keyPlaceholder: 'user:password', keyHint: 'Business One: a Service Layer user. S/4HANA: a communication user on an arrangement for API_BUSINESS_PARTNER + API_OPLACCTGDOCITEMCUBE_SRV. Paste user and password joined by a colon.', secondaryField: { bodyKey: 'sap_url', placeholder: 'https://sap.example.com:50000/b1s/v2/?CompanyDB=SBODEMOUS', hint: 'Business One: the Service Layer URL with ?CompanyDB=. S/4HANA: the API host, e.g. https://my123456-api.s4hana.cloud.sap (add ?sap-client=100 on-prem). Must be public HTTPS.' } },
  { id: 'recurly', provider: 'recurly', name: 'Recurly', icon: '♻️', description: 'Subscription invoices, accounts, and AR aging', flow: 'api_key', keyPlaceholder: 'Your Recurly private API key...', keyHint: 'Recurly → Integrations → API credentials → Private API key', ...HIDDEN },
  { id: 'zendesk', provider: 'zendesk', name: 'Zendesk', icon: '🎫', description: 'Support tickets and churn-risk signals', flow: 'api_key', keyPlaceholder: 'you@company.com:api_token', keyHint: 'Zendesk → Admin Center → Apps and integrations → Zendesk API → add an API token, then paste "your email : token" joined by a colon', secondaryField: { bodyKey: 'subdomain', placeholder: 'your-company', hint: 'The {subdomain} in {subdomain}.zendesk.com' } },
  { id: 'intercom', provider: 'intercom', name: 'Intercom', icon: '💬', description: 'Conversations and churn-risk signals', flow: 'api_key', keyPlaceholder: 'Your Intercom access token...', keyHint: 'Intercom → Settings → Integrations → Developer Hub → your app → Authentication → copy the access token' },
  { id: 'freshdesk', provider: 'freshdesk', name: 'Freshdesk', icon: '🎟️', description: 'Support tickets and churn-risk signals', flow: 'api_key', keyPlaceholder: 'Your Freshdesk API key...', keyHint: 'Freshdesk → Profile settings → View API key', secondaryField: { bodyKey: 'subdomain', placeholder: 'your-company', hint: 'The {subdomain} in {subdomain}.freshdesk.com' } },
  { id: 'klaviyo', provider: 'klaviyo', name: 'Klaviyo', icon: '📮', description: 'Email campaigns and audience insights', flow: 'api_key', keyPlaceholder: 'pk_...', keyHint: 'Klaviyo → Account → Settings → API keys → create a read-only private key' },
  { id: 'calcom', provider: 'calcom', name: 'Cal.com', icon: '📆', description: 'Scheduled bookings and meeting sync', flow: 'api_key', keyPlaceholder: 'cal_...', keyHint: 'Cal.com → Settings → Developer → API keys' },
  { id: 'acuity', provider: 'acuity', name: 'Acuity Scheduling', icon: '⏰', description: 'Appointments and meeting sync', flow: 'api_key', keyPlaceholder: 'Your Acuity API key...', keyHint: 'Acuity → Integrations → API → show API credentials', secondaryField: { bodyKey: 'user_id', placeholder: 'User ID', hint: 'Shown next to your API key' } },
  { id: 'pandadoc', provider: 'pandadoc', name: 'PandaDoc', icon: '📄', description: 'Proposal tracking — sent, viewed, and stalled signals', flow: 'api_key', keyPlaceholder: 'Your PandaDoc API key...', keyHint: 'PandaDoc → Settings → API & integrations → request/copy the production API key' },
  { id: 'zoho', provider: 'zoho', name: 'Zoho CRM', icon: '🟨', description: 'CRM — deals, contacts, and pipelines', flow: 'oauth' },
  { id: 'asana', provider: 'asana', name: 'Asana', icon: '🎯', description: 'Projects, tasks, and overdue-work signals', flow: 'api_key', keyPlaceholder: 'Your Asana personal access token...', keyHint: 'Asana → Settings → Apps → Developer apps → create a personal access token' },
  { id: 'clickup', provider: 'clickup', name: 'ClickUp', icon: '✅', description: 'Tasks, lists, and overdue-work signals', flow: 'api_key', keyPlaceholder: 'pk_...', keyHint: 'ClickUp → Settings → Apps → generate a personal API token' },
  { id: 'activecampaign', provider: 'activecampaign', name: 'ActiveCampaign', icon: '📨', description: 'Email marketing campaigns and audience insights', flow: 'api_key', keyPlaceholder: 'Your ActiveCampaign API key...', keyHint: 'ActiveCampaign → Settings → Developer → copy the API key', secondaryField: { bodyKey: 'ac_account', placeholder: 'youraccount (from youraccount.api-us1.com)', hint: 'Settings → Developer → API URL — the part before .api-us1.com' } },
  { id: 'instantly', provider: 'instantly', name: 'Instantly', icon: '⚡', description: 'Cold-email campaigns, reply rates, and deliverability signals', flow: 'api_key', keyPlaceholder: 'Your Instantly API key...', keyHint: 'Instantly → Settings → Integrations → API keys → create a key with the campaigns:read (or all:read) scope' },
  { id: 'gong', provider: 'gong', name: 'Gong', icon: '🎙️', description: 'Revenue-intelligence calls and transcripts', flow: 'api_key', keyPlaceholder: 'accessKey:accessKeySecret', keyHint: 'Gong → Company Settings → Ecosystem → API → create a key, then paste "access key : secret" joined by a colon' },
  { id: 'webex', provider: 'webex', name: 'Webex', icon: '🟢', description: 'Meetings, recordings and transcripts', flow: 'oauth' },
  { id: 'adobesign', provider: 'adobesign', name: 'Adobe Acrobat Sign', icon: '✍️', description: 'Send SOWs for e-signature and track status', flow: 'oauth' },
  { id: 'agiloft', provider: 'agiloft', name: 'Agiloft', icon: '📜', description: 'Contract lifecycle — SOW records and signed agreements', flow: 'api_key', keyPlaceholder: 'login:password', keyHint: 'A REST-enabled Agiloft user (Admin or Standard license) — ask your Agiloft admin. Paste login and password joined by a colon.', secondaryField: { bodyKey: 'kb_url', placeholder: 'https://yourcompany.agiloft.com/YOUR_KB', hint: 'Your Agiloft knowledgebase URL (host + KB name)' } },
  { id: 'clearbit', provider: 'clearbit', name: 'Clearbit', icon: '🔍', description: 'Person + company enrichment (legacy API keys — new access is HubSpot Breeze)', flow: 'api_key', keyPlaceholder: 'sk_...', keyHint: 'Works with an existing Clearbit secret API key. Clearbit no longer issues new standalone keys — new enrichment access is sold as HubSpot Breeze Intelligence' },
  { id: 'clay', provider: 'clay', name: 'Clay', icon: '🧱', description: 'Push prospects to a Clay table; enriched rows flow back automatically', flow: 'webhook_url', keyPlaceholder: 'https://api.clay.com/v3/sources/webhook/...', keyHint: 'In your Clay table: + Add → Import data from webhook → copy the webhook URL', secondaryField: { bodyKey: 'clay_auth_token', placeholder: 'Webhook auth token (optional)', hint: 'Shown once when you enable auth on the Clay webhook', optional: true } },
  { id: 'zapier', provider: 'zapier', name: 'Zapier', icon: '🔶', description: 'Send briefings, signals, and action events to any Zap', flow: 'webhook_url', keyPlaceholder: 'https://hooks.zapier.com/hooks/catch/...', keyHint: 'Zapier → new Zap → trigger "Webhooks by Zapier" → Catch Hook → copy the webhook URL' },
  { id: 'n8n', provider: 'n8n', name: 'n8n', icon: '🔗', description: 'Trigger n8n workflows from briefings, signals, and action events', flow: 'webhook_url', keyPlaceholder: 'https://your-instance.app.n8n.cloud/webhook/...', keyHint: 'n8n → add a Webhook trigger node → copy its production URL (path contains /webhook/)' },
  { id: 'constantcontact', provider: 'constantcontact', name: 'Constant Contact', icon: '✉️', description: 'Email marketing campaigns and audience insights', flow: 'oauth' },
  { id: 'freshbooks', provider: 'freshbooks', name: 'FreshBooks', icon: '🍃', description: 'Invoices, clients, and AR aging', flow: 'oauth', ...HIDDEN },
  { id: 'double', provider: 'double', name: 'Double (Keeper)', icon: '📒', description: 'Bookkeeping close tasks and practice workload signals', flow: 'api_key', keyPlaceholder: 'clientId:clientSecret', keyHint: 'Double → Practice Settings → Practice Management → General → Generate API key, then paste "Client ID : Client Secret" joined by a colon' },
];

// ── Tile health ──────────────────────────────────────────────────────────────
// What the Settings tile face says about a connected row. `last_sync_status`
// is free text by convention: 'ok', 'ok: 0 new', 'partial: …', 'error: …'.

export const SYNC_FRESH_MS = 24 * 60 * 60 * 1000;

export type IntegrationHealth =
  | { state: 'ok' }
  | { state: 'reconnect' }
  | { state: 'attention'; detail: string };

export function integrationHealth(conn: ApiIntegration, now = Date.now()): IntegrationHealth {
  if (conn.status === 'revoked') return { state: 'reconnect' };
  const status = conn.last_sync_status ?? '';
  if (!conn.last_synced_at) return { state: 'attention', detail: 'Not synced yet — press Sync Now' };
  if (!/^ok\b/i.test(status)) {
    // Strip the leading status word so the tile shows the message itself.
    const detail = status.replace(/^(error|partial|failed)\s*:?\s*/i, '').trim();
    return { state: 'attention', detail: detail || 'Last sync did not complete' };
  }
  const age = now - new Date(conn.last_synced_at).getTime();
  if (age > SYNC_FRESH_MS) {
    return { state: 'attention', detail: `Last synced ${Math.floor(age / SYNC_FRESH_MS)}d ago` };
  }
  return { state: 'ok' };
}

/** Look up an integration definition by provider key or ID. */
export function findIntegrationDef(providerOrId: string): IntegrationDef | undefined {
  return INTEGRATION_DEFS.find(d => d.provider === providerOrId || d.id === providerOrId);
}

// ── Use-case categories ──────────────────────────────────────────────────────
// Drives the Settings page segmentation. Keyed by def id so the def list
// itself stays untouched. Unlisted ids fall back to 'More'.

export const INTEGRATION_CATEGORIES = [
  'CRM & Sales',
  'Finance & Billing',
  'Marketing & Outreach',
  'Meetings & Scheduling',
  'Email & Comms',
  'Support & Success',
  'Projects & Docs',
  'Automation',
] as const;
export type IntegrationCategory = (typeof INTEGRATION_CATEGORIES)[number] | 'More';

const CATEGORY_BY_ID: Record<string, IntegrationCategory> = {
  hubspot: 'CRM & Sales', salesforce: 'CRM & Sales', close: 'CRM & Sales',
  gohighlevel: 'CRM & Sales', pipedrive: 'CRM & Sales', zoho: 'CRM & Sales',
  pandadoc: 'CRM & Sales', adobesign: 'CRM & Sales',
  quickbooks: 'Finance & Billing', xero: 'Finance & Billing', freshbooks: 'Finance & Billing',
  stripe: 'Finance & Billing', square: 'Finance & Billing', shopify: 'Finance & Billing',
  woocommerce: 'Finance & Billing', paypal: 'Finance & Billing', chargebee: 'Finance & Billing',
  recurly: 'Finance & Billing', double: 'Finance & Billing', sap: 'Finance & Billing',
  mailchimp: 'Marketing & Outreach', klaviyo: 'Marketing & Outreach', activecampaign: 'Marketing & Outreach',
  constantcontact: 'Marketing & Outreach', instantly: 'Marketing & Outreach', apollo: 'Marketing & Outreach',
  clearbit: 'Marketing & Outreach', clay: 'Marketing & Outreach',
  gsc: 'Marketing & Outreach', ga4: 'Marketing & Outreach',
  youtube: 'Marketing & Outreach', gbp: 'Marketing & Outreach',
  fireflies: 'Meetings & Scheduling', gong: 'Meetings & Scheduling', zoom: 'Meetings & Scheduling',
  webex: 'Meetings & Scheduling',
  calendly: 'Meetings & Scheduling', calcom: 'Meetings & Scheduling', acuity: 'Meetings & Scheduling',
  google_cal: 'Meetings & Scheduling', outlook_cal: 'Meetings & Scheduling',
  gmail: 'Email & Comms', outlook: 'Email & Comms', slack: 'Email & Comms', teams: 'Email & Comms',
  zendesk: 'Support & Success', intercom: 'Support & Success', freshdesk: 'Support & Success',
  linear: 'Projects & Docs', notion: 'Projects & Docs', monday: 'Projects & Docs',
  asana: 'Projects & Docs', clickup: 'Projects & Docs',
  agiloft: 'Projects & Docs',
  zapier: 'Automation', n8n: 'Automation',
};

export function categoryOf(def: IntegrationDef): IntegrationCategory {
  return CATEGORY_BY_ID[def.id] ?? 'More';
}

// ── Hybrid connect fallbacks ─────────────────────────────────────────────────
// OAuth providers whose vendor app isn't registered on this deployment (no
// CLIENT_ID secret) normally render "Coming soon". These vendors ALSO accept
// a user-supplied credential, so the card offers that path instead — the
// integration works today, and OAuth takes over transparently once the
// operator registers the app.

export interface ConnectFallback {
  flow: 'api_key' | 'webhook_url';
  /** Override the connect endpoint (defaults to /v1/integrations/{provider}/connect). */
  connectPath?: string;
  keyPlaceholder: string;
  keyHint: string;
}

export const CONNECT_FALLBACKS: Record<string, ConnectFallback> = {
  linear: {
    flow: 'api_key',
    keyPlaceholder: 'lin_api_...',
    keyHint: 'Linear → Settings → Security & access → Personal API keys → create a key',
  },
  notion: {
    flow: 'api_key',
    keyPlaceholder: 'ntn_... or secret_...',
    keyHint: 'notion.so/my-integrations → create an internal integration → copy the token, then share the pages/databases you want synced with that integration',
  },
  mailchimp: {
    flow: 'api_key',
    keyPlaceholder: 'xxxxxxxxxx-us21',
    keyHint: 'Mailchimp → Account & billing → Extras → API keys → create a key',
  },
  slack: {
    flow: 'webhook_url',
    connectPath: '/v1/integrations/slack/legacy/connect',
    keyPlaceholder: 'https://hooks.slack.com/services/...',
    keyHint: 'Create an Incoming Webhook at api.slack.com/messaging/webhooks and paste the URL — briefings, signals, and action events post to the channel you pick',
  },
};

// ── API wrapper functions ────────────────────────────────────────────────────
// These call the Cloudflare Worker via the shared `api` helper.

/**
 * A connected integration row as returned by GET /v1/integrations.
 * Alias of ApiIntegration — exposed under a stable name so consuming
 * components can import a single symbol from this module.
 */
export type IntegrationRow = ApiIntegration;

/**
 * An email signal surfaced by the inbox-triage ML pipeline.
 * Returned by GET /v1/integrations/gmail/signals.
 */
export interface EmailSignal {
  id: string;
  signal_type:
    | 'overdue_followup'
    | 'unhandled_inbound'
    | 'high_priority_unread'
    | 'human_question_pending'
    | 'reply_from_at_risk_client'
    | 'mentions_competitor'
    | 'large_attachment';
  severity: 'high' | 'med' | 'low';
  title: string;
  context: string | null;
  metadata: Record<string, unknown> | null;
  surfaced_at: string | null;
  thread_id: string | null;
  dismissed_at: string | null;
  external_thread_id: string | null;
  thread_subject: string | null;
  thread_sender: string | null;
  /** Which mailbox holds the thread. */
  provider: EmailProvider | null;
  /** Deep link into that mailbox's web client (Gmail or Outlook on the web). */
  open_url: string | null;
  /** @deprecated same value as open_url — kept for older clients. */
  gmail_url: string | null;
}

// ── Email providers (mailboxes) ──────────────────────────────────────────────
// Everything email/calendar-shaped in the UI should reason in terms of "the
// connected mailbox", not Gmail specifically. Both providers land in the same
// signals / drafts / calendar surfaces.

export type EmailProvider = 'gmail' | 'outlook';
export const EMAIL_PROVIDERS: readonly EmailProvider[] = ['gmail', 'outlook'];
export const EMAIL_PROVIDER_LABEL: Record<EmailProvider, string> = { gmail: 'Gmail', outlook: 'Outlook' };

export function isEmailProvider(p: string | null | undefined): p is EmailProvider {
  return p === 'gmail' || p === 'outlook';
}

/** The account's active mailbox integration row, if any (newest first). */
export function findEmailIntegration(rows: ApiIntegration[] | undefined): ApiIntegration | undefined {
  return (rows ?? []).find(r => isEmailProvider(r.provider) && r.status === 'active');
}

/**
 * Start the OAuth flow for a mailbox provider. Returns the provider's
 * authorization URL; the caller sets `window.location.href` to it.
 */
export async function getEmailAuthUrl(provider: EmailProvider, returnTo?: string): Promise<string> {
  const path = returnTo
    ? `/v1/integrations/${provider}/connect?returnTo=${encodeURIComponent(returnTo)}`
    : `/v1/integrations/${provider}/connect`;
  const res = await api<{ authUrl: string }>(path);
  return res.authUrl;
}

/** Fetch all connected integrations for the current account. */
export async function listIntegrations(): Promise<IntegrationRow[]> {
  const res = await api<{ integrations: IntegrationRow[] }>('/v1/integrations');
  return res.integrations ?? [];
}

/** What the account lets Waypoint see (connected AND synced, per domain): the API's own verdict, never re-derived here. */
export async function getIntegrationCoverage(): Promise<SourceCoverage | null> {
  const res = await api<{ coverage?: SourceCoverage }>('/v1/integrations');
  return res.coverage ?? null;
}

/**
 * Which OAuth providers are actually configured on the Worker (have their
 * CLIENT_ID secret). Keyed by provider. Un-listed providers (API-key flows)
 * are always available. Used to render un-configured providers as "Coming
 * soon" instead of a Connect button that errors with <provider>_not_configured.
 */
export async function fetchAvailability(): Promise<Record<string, boolean>> {
  try {
    const res = await api<{ availability: Record<string, boolean> }>('/v1/integrations/availability');
    return res.availability ?? {};
  } catch {
    return {};
  }
}

/**
 * Start the Gmail OAuth flow. Returns the Google authorization URL.
 * The caller should set `window.location.href` to this URL.
 *
 * @param returnTo  Path to redirect back to after the OAuth callback
 *                  (defaults to the current page).
 */
export async function getGmailAuthUrl(returnTo?: string): Promise<string> {
  return getEmailAuthUrl('gmail', returnTo);
}

/** Start the Microsoft 365 / Outlook OAuth flow. */
export async function getOutlookAuthUrl(returnTo?: string): Promise<string> {
  return getEmailAuthUrl('outlook', returnTo);
}

/**
 * Trigger a Gmail inbox sync for the current account.
 *
 * @param days  How many days of history to pull (1–90, default 30).
 */
export async function syncGmail(days = 30): Promise<{ ok: boolean; threadsUpserted?: number; signalsCreated?: number }> {
  return api<{ ok: boolean; threadsUpserted?: number; signalsCreated?: number }>(
    `/v1/integrations/gmail/sync?days=${days}`,
    { method: 'POST' }
  );
}

/** Providers whose synced data is deleted on disconnect (Google Limited Use / Microsoft). */
const PURGED_ON_DISCONNECT: Record<string, string> = {
  gmail: 'Your Google account is disconnected and the Gmail, Google Calendar, Search Console, Analytics, Business Profile and YouTube data Waypoint synced from it is permanently deleted.',
  calendar: 'Google Calendar runs on your Google connection: disconnecting removes that connection and permanently deletes the Gmail, calendar and other Google data Waypoint synced.',
  gsc: 'Google data runs on one Google connection: disconnecting removes it and permanently deletes the Google data Waypoint synced.',
  ga4: 'Google data runs on one Google connection: disconnecting removes it and permanently deletes the Google data Waypoint synced.',
  gbp: 'Google data runs on one Google connection: disconnecting removes it and permanently deletes the Google data Waypoint synced.',
  youtube: 'Google data runs on one Google connection: disconnecting removes it and permanently deletes the Google data Waypoint synced.',
  outlook: 'The Outlook / Microsoft 365 connection is removed and the email, calendar and Teams chat data Waypoint synced from it is permanently deleted.',
  outlook_calendar: 'Outlook Calendar runs on your Microsoft connection: disconnecting removes it and permanently deletes the email, calendar and Teams chat data Waypoint synced.',
};

/** Confirm-dialog copy for disconnecting `provider`; a default for everything else. */
export function disconnectDescription(provider: string): string {
  return PURGED_ON_DISCONNECT[provider] ?? 'This will stop syncing data. You can reconnect at any time.';
}

/** Disconnect Gmail for the current account (revokes tokens). */
export async function disconnectGmail(): Promise<void> {
  await api('/v1/integrations/gmail', { method: 'DELETE' });
}

/** Fetch active (un-dismissed) email signals for the current account — from
 *  every connected mailbox (Gmail and/or Outlook). The path is historical. */
export async function listSignals(): Promise<EmailSignal[]> {
  const res = await api<{ signals: EmailSignal[] }>('/v1/integrations/gmail/signals');
  return res.signals ?? [];
}

/** Dismiss a signal by ID so it no longer appears in the inbox triage panel. */
export async function dismissSignal(id: string): Promise<void> {
  await api(`/v1/integrations/gmail/signals/${id}/dismiss`, { method: 'POST' });
}

/**
 * The flow a connector should RENDER as, given which OAuth apps the Worker has
 * registered (fetchAvailability).
 *
 * An OAuth provider whose CLIENT_ID is unset answers /connect with 503
 * `<provider>_not_configured`, so offering a Connect button is a dead end. The
 * owner Settings screen has always gated on this; the Sales shell lost the
 * guard in 71222b13 ("cut what's fake") and could offer a Connect that 503s.
 *
 * Unknown availability (the call failed, or the provider is api_key) is treated
 * as AVAILABLE on purpose: a transient failure must hide nothing. Only an
 * explicit `false` downgrades a connector.
 */
export function effectiveFlow<F extends string>(
  flow: F,
  provider: string,
  availability: Record<string, boolean> | undefined,
): F | 'unavailable' {
  if (flow !== 'oauth') return flow;
  return availability?.[provider] === false ? 'unavailable' : flow;
}
